import { useState } from 'react';
import { Upload, Button, message, Card, Progress, Statistic, Row, Col, Spin } from 'antd';
import { UploadOutlined, InboxOutlined, CheckCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import Header from '../Header';
import Sidebar from '../Sidebar';
import FeatherIcon from "feather-icons-react";
import { baseAPI } from '../../api/base.api';

const { Dragger } = Upload;

const UploadXML = () => {
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResult, setUploadResult] = useState(null);

    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.xml',
        fileList,
        showUploadList: false, // Hide default file list since we show custom one
        beforeUpload: (file) => {
            const isXML = file.type === 'text/xml' || file.name.toLowerCase().endsWith('.xml');
            if (!isXML) {
                message.error('Можете да качвате само XML файлове!');
                return false;
            }
            return false; // Prevent automatic upload
        },
        onChange: (info) => {
            setFileList(info.fileList.slice(-1)); // Keep only the latest file
            setUploadResult(null); // Clear previous results
            setUploadProgress(0); // Reset progress
        },
        onDrop(e) {
            
        },
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.warning('Моля, изберете XML файл за качване!');
            return;
        }
        
        const formData = new FormData();
        formData.append('file', fileList[0].originFileObj);
        
        setUploading(true);
        setUploadProgress(0);
        // Don't clear results here - only clear on new file selection
        
        try {
            const response = await baseAPI.post('/xml/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 300000, // 5 minutes timeout for large files
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(progress);
                },
            });
            
            if (response.success) {
                message.success('XML файлът е обработен успешно!');
                setUploadResult(response);
            } else {
                message.error(response.message || 'Грешка при обработка на файла');
            }
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.message || 'Грешка при качване на файла';
            message.error(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setFileList([]);
        setUploadResult(null);
    };

    return (
        <>
            <Header />
            <Sidebar activeClassName="upload-xml" />
            <div className="page-wrapper">
                <div className="content">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <a href="/">Главна</a>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Зареди файл</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Upload Content */}
                    <div className="row">
                        <div className="col-lg-12">
                            <Card title="Качване на XML файл" className="mb-4">
                                <div className="row">
                                    <div className="col-md-8 mx-auto">
                                        {!uploading && !uploadResult && (
                                            <>
                                                {fileList.length === 0 ? (
                                                    <Dragger {...uploadProps} style={{ padding: '40px 20px' }}>
                                                        <p className="ant-upload-drag-icon">
                                                            <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                                                        </p>
                                                        <p className="ant-upload-text" style={{ fontSize: '16px', fontWeight: '500' }}>
                                                            Кликнете или плъзнете XML файл тук за качване
                                                        </p>
                                                        <p className="ant-upload-hint" style={{ color: '#666' }}>
                                                            Поддържат се само XML файлове. Файлът ще бъде обработен автоматично.
                                                        </p>
                                                    </Dragger>
                                                ) : (
                                                    <div style={{ padding: '40px 20px', border: '2px dashed #d9d9d9', borderRadius: '6px', backgroundColor: '#fafafa', textAlign: 'center' }}>
                                                        <div style={{ marginBottom: '24px' }}>
                                                            <FileTextOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                                                            <div>
                                                                <strong style={{ fontSize: '16px', color: '#262626', display: 'block', marginBottom: '4px' }}>
                                                                    {fileList[0].name}
                                                                </strong>
                                                                <span style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                                                    Размер: {(fileList[0].size / 1024).toFixed(2)} KB
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <Button 
                                                                type="primary" 
                                                                size="large"
                                                                icon={<UploadOutlined />}
                                                                onClick={handleUpload}
                                                                style={{ marginRight: '12px' }}
                                                            >
                                                                Качи XML файл
                                                            </Button>
                                                            <Button 
                                                                type="default" 
                                                                onClick={handleRemove}
                                                                size="large"
                                                            >
                                                                Премахни
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Upload Progress */}
                                        {uploading && (
                                            <div className="mt-4">
                                                <Card size="small">
                                                    <div className="text-center">
                                                        {uploadProgress < 100 ? (
                                                            <>
                                                                <p style={{ marginBottom: '16px', fontWeight: '500' }}>
                                                                    Качване на файл...
                                                                </p>
                                                                <Progress
                                                                    percent={uploadProgress}
                                                                    status="active"
                                                                    strokeColor="#1890ff"
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Spin size="large" />
                                                                <p style={{ marginTop: '16px', fontWeight: '500' }}>
                                                                    Обработване на XML файла...
                                                                </p>
                                                                <p style={{ color: '#666', fontSize: '14px' }}>
                                                                    Моля изчакайте, докато данните се импортират
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </Card>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </Card>

                            {/* Upload Results */}
                            {uploadResult && !uploading && (
                                <Card title="Резултати от импорта" className="mt-4">
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} sm={12} md={8}>
                                            <Card size="small">
                                                <Statistic 
                                                    title="Практики" 
                                                    value={uploadResult.practicesCreated || 0}
                                                    prefix={<FileTextOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Card size="small">
                                                <Statistic 
                                                    title="Лекари" 
                                                    value={uploadResult.doctorsCreated || 0}
                                                    prefix={<FileTextOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Card size="small">
                                                <Statistic 
                                                    title="Пациенти" 
                                                    value={uploadResult.patientsCreated || 0}
                                                    prefix={<FileTextOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Card size="small">
                                                <Statistic 
                                                    title="Прегледи" 
                                                    value={uploadResult.visitsCreated || 0}
                                                    prefix={<FileTextOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Card size="small">
                                                <Statistic 
                                                    title="Диагнози" 
                                                    value={uploadResult.diagnosesCreated || 0}
                                                    prefix={<FileTextOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Card size="small">
                                                <Statistic 
                                                    title="Рецепти" 
                                                    value={uploadResult.prescriptionsCreated || 0}
                                                    prefix={<FileTextOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                    </Row>
                                    <div className="text-center mt-3">
                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '24px', marginRight: '8px' }} />
                                        <span style={{ color: '#52c41a', fontSize: '16px', fontWeight: '500' }}>
                                            {uploadResult.message || 'XML файлът е импортиран успешно!'}
                                        </span>
                                    </div>
                                    <div className="text-center mt-4">
                                        <Button 
                                            type="primary" 
                                            size="large"
                                            onClick={() => {
                                                setUploadResult(null);
                                                setFileList([]);
                                                setUploadProgress(0);
                                            }}
                                        >
                                            Качи друг XML файл
                                        </Button>
                                    </div>
                                </Card>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UploadXML;